import { action, observable, runInAction, when } from 'mobx';
import * as moment from 'moment-timezone';
import { IWell } from 'src/Interfaces/WellInterface';
import { API } from 'src/Services/Api/Api';
import { ApprovalApi } from 'src/Stores/Tests/ApprovalApi';
import { ITest } from '../../Interfaces/TestInterface';
import ConvertNulls from '../../Services/ConvertNulls';
import { CalcStore } from '../Calc/CalcStore';
import { MessageStore } from '../Messages/Messages';
import { RootStore } from '../Root/RootStore';




class TestsApi {
    public api: API;

    constructor(Api:API) {
        this.api = Api;
    }

    public getTests(wellId: number | string) {
        return this.api.get('/welltests/' + wellId + '/tests')
        .then(
            (data: any) => {
                return data;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getTestsCopy(wellId: number | string) {
        return this.api.get('/welltests/' + wellId + '/testscopylist');
    }

    public getTestsAllData(wellId: string) {
        return this.api.get('/welltests/' + wellId + '/all');
    }

    public getTestsAllDataReport(wellId: string) {
        return this.api.get('/welltests/' + wellId + '/reportall');
    }

    public getTestsListForReport(wellId: string) {
        return this.api.get('/welltests/' + wellId + '/reporttestlist');
    }

    public getTest(testId: string, wellId: number | string) {
        return this.api.get('/welltests/' + wellId + '/tests/' + testId)
        .then(
            (data: any) => {
                return data;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getTestCopy(testId: string, wellId: number | string) {
        return this.api.get('/welltests/' + wellId + '/tests/' + testId + '/copydata');
    }

    public getTestReport(testId: string, wellId: number | string) {
        return this.api.get('/welltests/' + wellId + '/tests/' + testId + '/report');
    }

    public postTest(test: ITest, wellId: number | string) {
        return this.api.post('/welltests/' + wellId + '/tests', test)
        .then(
            (data: any) => data,
            (error: any) => { throw error; }
        );
    }

    public putTest(test: ITest, wellId: number | string) {
        return this.api.put('/welltests/' + wellId + '/tests/' + test.id, test)
        .then(
            (data: any) => data,
            (error: any) => {throw error; }
        );
    }

    public deleteTest(testId: string, wellId: string) {
        return this.api.delete(`/welltests/${wellId}/tests/${testId}`);
    }

    public getMudTypes() {
        return this.api.get('/mudtypes')
        .then(
            (data: any) => {
                return data;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getDeviceNames(wellId: number | string) {
        return this.api.get('/solidscontrolequip/' + wellId)
        .then(
            (data: any) => {
                return data;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getIntervalTypes() {
        return this.api.get('/optionalfieldlists/intervalTypeOptions')
        .then(
            (data: any) => {
                const fieldListItems: string[] = [];
                data.optionalFieldListItems.forEach((item: any) => {
                    fieldListItems.push(item.fieldItemName);
                });
                return fieldListItems;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getIntervalSubTypes() {
        return this.api.get('/optionalfieldlists/subIntervalTypeOptions')
        .then(
            (data: any) => {
                const fieldListItems: string[] = [];
                data.optionalFieldListItems.forEach((item: any) => {
                    fieldListItems.push(item.fieldItemName);
                });
                return fieldListItems;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getToolsDescriptionOptions() {
        return this.api.get('/optionalfieldlists/ToolsDescriptionOptions')
        .then(
            (data: any) => {
                const fieldListItems: string[] = [];
                data.optionalFieldListItems.forEach((item: any) => {
                    fieldListItems.push(item.fieldItemName);
                });
                return fieldListItems;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getDrillStringItemTypes() {
        return this.api.get('/drillstrings/itemtypes')
        .then(
            (data: any) => {
                return data;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getProductList(wellId: number | string) {
        return this.api.get('/products/inventory/' + wellId)
        .then(
            (data: any) => {
                return data;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getProductMasterList() {
        return this.api.get('/products/inventory');
    }

    public getWeightingAgents(wellId: string) {
        return this.api.get('/products/weightingagents/' + wellId);
    }

    public getExpenseTypes(wellId?: string) {
        let urlAddition = '';
        if (wellId) {
            urlAddition = `/inventory/${wellId}`;
        }
        return this.api.get(`/expensetypes${urlAddition}`)
        .then(
            (data: any) => {
                return data;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getProductCategories() {
        return this.api.get('/categorys')
        .then(
            (data: any) => {
                return data;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getMudPropertyOptions() {
        return this.api.get('/optionalfieldlists/mudpropertyoptions')
        .then(
            (data: any) => {
                return data.optionalFieldListItems;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getMudTestOptions() {
        return this.api.get('/optionalfieldlists/mudtestoptions')
        .then(
            (data: any) => {
                return data.optionalFieldListItems;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getStorageTypeOptions() {
        return this.api.get('/optionalfieldlists/storagetypeoptions')
        .then(
            (data: any) => {
                return data.optionalFieldListItems;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getOilFluidSubTypeOptions() {
        return this.api.get('/optionalfieldlists/OilFluidSubTypeOptions')
        .then(
            (data: any) => {
            return data.optionalFieldListItems;
            },
            (error: any) => {
                throw error;
            }
        );
    }

    public getWaterFluidSubTypeOptions() {
        return this.api.get('/optionalfieldlists/WaterFluidSubTypeOptions')
        .then(
            (data: any) => {
            return data.optionalFieldListItems;
            },
            (error: any) => {
                throw error;
            }
        );
    }
}

export class TestsStore {
    @observable
    public savingTest: boolean = false;
    public convertNulls = new ConvertNulls();
    public api :TestsApi;
    private approvalApi: ApprovalApi;
    private calcs: CalcStore;
    private messages: MessageStore;
    private unassignedTest: ITest;
    @observable public tests: ITest[];
    @observable public testsSelectedForPdf: ITest[];
    @observable public test: ITest;
    @observable public testsAll: ITest[];
    @observable public copyTest: ITest;

    private RootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.RootStore = rootStore;
        this.messages = this.RootStore.MessageStore;
        this.calcs = this.RootStore.CalcStore;
        this.api = new TestsApi(this.RootStore.Api);
        this.approvalApi = new ApprovalApi(this.RootStore.Api);
    }

    public createNewTestInstance() {
        const test = new ITest();
        this.removeZeros(test);
        runInAction(() => {
            this.test = test;
        });
        return test;
    }

    @action
    public clearTestStore = () => {
        this.testsAll = undefined as unknown as ITest[];
        this.tests = [] as ITest[];
        this.test = this.unassignedTest;
    }

    private removeZeros(obj: any) {
        for (const prop in obj) {
            if(Array.isArray(obj[prop])) {
                for (const arrayObj of obj[prop]) {
                    this.removeZeros(arrayObj);
                }
            } else if(typeof obj[prop] === 'object' && prop !== 'pumpData') {
                this.removeZeros(obj[prop]);
            } else if(obj[prop] === 0) {
                obj[prop] = '';
            }
        }
    }
    public getTests = async (wellId: number | string) => {
        await when(() => this.savingTest === false);
        const list: ITest[] = await this.api.getTests(wellId);
        runInAction(() => {
            this.tests = list;
        });
        return list;
    }

    public getTestsCopy = async (wellId: number | string) => {
        await when(() => this.savingTest === false);
        const list: ITest[] = await this.api.getTestsCopy(wellId);
        return list;
    }

    public getAllTestsDetails = async ( wellId: string ) => {
        const list = await this.api.getTestsAllData(wellId);
        runInAction(() => {
            this.tests = list;
        });

        return list;
    }

    public getAllTestsEmail = async ( wellId: string ) => {
        const list = await this.api.getTestsAllData(wellId);
        runInAction(() => {
            this.testsAll = list;
        });

        return list;
    }
    public getAllTestsDetailsReport = async ( wellId: string ) => {
        const list = await this.api.getTestsAllDataReport(wellId);
        runInAction(() => {
            this.tests = list;
        });

        return list;
    }

    public getTestListForReport = async ( wellId: string ) => {
        const list = await this.api.getTestsListForReport(wellId);
        runInAction(() => {
            this.tests = list;
        });

        return list;
    }

    public clearTest = async () => {
        this.test = this.unassignedTest;
        await this.test;
    }

    public enableRecentTest = () => {
        if (this.tests && this.tests.length > 0) {
            return this.tests[0].id === this.test.id;
        }
        return false;
    }

    @action
    public deleteTest = async (testId: string, wellId: string ) => {
        this.savingTest = true;
        try {
            const data = await this.api.deleteTest(testId, wellId);
            this.getTests(wellId);
            if (data) {
                this.messages.addMessage(
                    'Test successfully deleted',
                    'success'
                );
                this.createNewTestInstance();
            }
            return data;
        } finally {
            this.savingTest = false;
        }
    }
    public getTestDetails = async (testId: string, wellId: number | string) => {
        await when(() => this.savingTest === false);
        if (testId === 'new') {
            return this.createNewTestInstance();
        }
        const gotTest = await this.api.getTest(testId, wellId) as ITest;
        runInAction(() => {
            const noNulls = this.convertNulls.recurseObject(gotTest) as ITest;
            this.test = noNulls;
        });
        this.checkForOutstandingDispatches(gotTest);
        
        return gotTest;
    }

    private checkForOutstandingDispatches = (test: ITest) => {
        let needMessage = false;
        if (test && !test.isInventoryReport) {
            return;
        }
        if (test && test.deliveriesPending) {
            if (test.deliveriesPending.some(dp => dp.trucks.some(t => t.status === 'EnRoute'))) {
                needMessage = true;
            }
        }
        if (test && test.pickupsPending) {
            if (test.pickupsPending.some(pp => pp.trucks.some(t => !t.returnedWellTestId && !t.returnedWellCloseReviewId))) {
                needMessage = true;
            }
        }

        if (needMessage) {
            this.RootStore.MessageStore.addMessage('Please acknowledge all trucks that have delivered or picked up items on the Inventory tab of this well test.', 'warning');
        }
    }

    public getCopyTestDetails = async (testId: string, wellId: number | string) => {
        await when(() => this.savingTest === false);
        const gotTest = await this.api.getTestCopy(testId, wellId);
        runInAction(() => {
            const noNulls = this.convertNulls.recurseObject(gotTest);
            this.copyTest = noNulls;
        });
        return gotTest;
    }

    public getTestDetailsReport = async (testId: string, wellId: number | string) => {
        const gotTest = await this.api.getTestReport(testId, wellId);
        runInAction(() => {
            const noNulls = this.convertNulls.recurseObject(gotTest);
            this.test = noNulls;
        });
        return gotTest;
    }

    public canDelete = () => {
        if (this.test && this.tests && this.tests[0]) {
            return this.test.id === this.tests[0].id;
        }
        return false;
    }

    public canCreateNewTest = () => {
        if (this.RootStore.UserStore.hasGenericPermission("Permissions.WellTests.Create")) {
            if (this.tests && this.tests[0] && this.tests[0].testStatus !== 'Approved') {
                if (this.RootStore.UserStore.hasGenericPermission("Permissions.WellTests.Approve")) {
                    if (this.RootStore.UserStore.hasPermission('Permissions.WellTests.Approve.All')) {
                        return true;
                    } else {
                        if (this.RootStore.WellsStore.well.engineers.some(wu => wu.id === this.RootStore.UserStore.user.id)) {
                            return true;
                        }
                    }
                }
            } else {
                return true;
            }
        }
        return false;
    }
    
    @action
    public postTest = async (test: ITest, wellId: number | string ) => {
        if (this.savingTest) {
            return;
        }
        this.savingTest = true;
        try {
            test.testDate = moment(test.preTestDate).unix();
            const testResult = await this.api.postTest(test, wellId);
            this.getTests(wellId);
            if ( testResult ) {
                this.messages.addMessage(
                    'Test successfully created.',
                    'success'
                    );
                    const noNulls = this.convertNulls.recurseObject(testResult);
                    this.test = noNulls;
                }
            return testResult;
        } finally {
            this.savingTest = false;
        }
    }

    @action
    public putTest = async (test: ITest, wellId: number | string ) => {
        if( !test.drillStringAssembly || test.drillStringAssembly.length === 0) {
            alert('You are unable to save your current Well Test because it doesn’t have any Drill String Items in its Assembly. Please add one or more Items to your current Assembly then Save so that you can update your Test.');
            return false;
        }
        if (this.savingTest) {
            return;
        }
        this.savingTest = true;
        try {

            this.test = test;
            this.redoCalculations();
            const testResult = await this.api.putTest(this.test, wellId);
            this.getTests(wellId);
            if ( testResult ) {
                this.messages.addMessage(
                    'Data successfully saved.',
                    'success'
                    );
                    const noNulls = this.convertNulls.recurseObject(testResult);
                    this.test = noNulls;
                }
                return testResult;
        } finally {
            this.savingTest = false;
        }
    }

    public updateTestModel = async (test: ITest, wellId: number | string) => {
        test.submittedDate = moment(Date.now()).unix();
        if (test.id === "") {
            return this.postTest(test, wellId);
        } else {
            return this.putTest(test, wellId);
        }
    }
    public getListData(wells: IWell[] | undefined) {
        if( wells && Array.isArray(wells)) {
            wells.forEach((value) => {
                this.getProductList(value.id);
                this.getExpenseTypes(value.id);
            });
        }
        this.getDeviceNames('');
        this.getDrillStringItemTypes();
        this.getIntervalSubTypes();
        this.getIntervalTypes();
        this.getMudPropertyOptions();
        this.getMudTestOptions();
        this.getMudTypes();
        this.getProductCategories();
        this.getStorageTypeOptions();
        this.getOilFluidSubTypeOptions();
        this.getWaterFluidSubTypeOptions();
        this.getExpenseTypes();
    }
    
    public getMudTypes() {
        return this.api.getMudTypes();
    }

    public getDeviceNames(wellId: number | string) {
        return this.api.getDeviceNames(wellId);
    }

    public getIntervalTypes() {
        return this.api.getIntervalTypes();
    }

    public getIntervalSubTypes() {
        return this.api.getIntervalSubTypes();
    }

    public getDrillStringItemTypes() {
        return this.api.getDrillStringItemTypes();
    }

    public getProductList(wellId: number | string) {
        return this.api.getProductList(wellId);
    }

    public getProductMasterList() {
        return this.api.getProductMasterList();
    }

    public getExpenseTypes(wellId?: string) {
        return this.api.getExpenseTypes(wellId);
    }

    public getProductCategories() {
        return this.api.getProductCategories();
    }

    public getMudPropertyOptions() {
        return this.api.getMudPropertyOptions();
    }

    public getMudTestOptions() {
        return this.api.getMudTestOptions();
    }

    public getStorageTypeOptions() {
        return this.api.getStorageTypeOptions();
    }

    public getToolsDescriptionOptions() {
        return this.api.getToolsDescriptionOptions();
    }
    public getOilFluidSubTypeOptions() {
        return this.api.getOilFluidSubTypeOptions();
    }

    public getWaterFluidSubTypeOptions() {
        return this.api.getWaterFluidSubTypeOptions();
    }

    public getWeightingAgents(wellId: string) {
        return this.api.getWeightingAgents(wellId);
    }

    public submitForApproval(testId: string) {
        return this.approvalApi.submitForApproval(testId)
            .then((data) => {
                this.getTestDetails(this.test.id, this.test.wellId);
                if(data.result) {
                    this.messages.addMessage(
                        'Test successfully submitted.',
                        'success'
                        );
                    if (data.notifyError) {
                        this.messages.addMessage(
                            'One or more notifications did not go through because of an invalid notifcation email and/or phone number',
                            'danger'
                        );
                    }
                } else {
                    this.messages.addMessage(
                        'Test did not submit.  It has already been submitted or an error has occurred.',
                        'warning'
                        );
                }
            });
    }

    public approveTest(testId: string) {
        return this.approvalApi.approveTest(testId)
            .then((data) => {
                if(data.result) {
                    this.messages.addMessage(
                        'Test successfully approved.',
                        'success'
                        );
                    if (data.notifyError) {
                        this.messages.addMessage(
                            'One or more notifications did not go through because of an invalid notifcation email and/or phone number',
                            'danger'
                        );
                    }
                } else {
                    this.messages.addMessage(
                        'Test did not approve.  It has already been approved or an error has occurred.',
                        'warning'
                        );
                }
            });
    }

    public rejectTest(testId: string) {
        return this.approvalApi.rejectTest(testId)
            .then((data) => {
                if(data.result) {
                    this.messages.addMessage(
                        'Test rejected.',
                        'success'
                        );
                    if (data.notifyError) {
                        this.messages.addMessage(
                            'One or more notifications did not go through because of an invalid notifcation email and/or phone number',
                            'danger'
                        );
                    }
                } else {
                    this.messages.addMessage(
                        'Test already rejected.',
                        'warning'
                        );
                }
            });
    }

    public getTestsForApproval() {
        return this.approvalApi.getTestsForApproval();
    }
    /**
     * This is to provide a way to update as many properties as needed
     * in the test object, without triggering obersavers
     * 
     * @param props [object] - an object of properties and their corresponding values
     *  to update in the test store
     */
    public updateTestProperties(props: object) {
        if ( this.test && typeof this.test !== 'undefined' ) {
            for (const prop in props ) {
                if ( this.test[prop] ) {
                    this.test[prop] = props[prop];
                }
            }
        }
    }

    public calculatePumpData(test: ITest) {
        const nValue = this.calcs.nValue(
            {
                theta3: Number(test.mudProperties.rpmDegF3),
                theta300: Number(test.mudProperties.rpmDegF300),
            });

        const kValue = this.calcs.kValue(
            {
                nValue,
                theta300: Number(test.mudProperties.rpmDegF300)
            });

        if (test.pumpData && test.pumpData.pumps) {
            test.pumpData.pumps.forEach((currPump) =>{
                let gallonsPerStroke = 0;
                if (Number(currPump.linerQty) === 2 ) {
                    gallonsPerStroke = this.calcs.duplexPumpFlowRateGallonsPerStroke({
                        linerDiameter: Number(currPump.linerSize),
                        rodDiameter: Number(currPump.rodSize),
                        strokeLength: Number(currPump.stkLength),
                        volumeEfficiency: Number(currPump.efficiency) / 100,
                    });
                } else if (Number(currPump.linerQty) >= 3) {
                    gallonsPerStroke = this.calcs.multiplexPumpFlowRateGallonsPerStroke({
                        linerDiameter: Number(currPump.linerSize),
                        linerQty: Number(currPump.linerQty),
                        strokeLength: Number(currPump.stkLength),
                        volumeEfficiency: Number(currPump.efficiency) / 100,
                    });
                }
                if (gallonsPerStroke) {
                    const currPumpbblStk = this.calcs.convertUnits({
                        conversionFactor: Number(this.calcs.barrelsPerGallon),
                        valueToConvert: gallonsPerStroke,
                    });
                    const currPumpbblMin = this.calcs.convertUnitFromPerStrokeToPerMinute(currPump.stkMin, currPump.bblStk);
                    const currPumpgalMin = this.calcs.convertUnitFromPerStrokeToPerMinute(currPump.stkMin, gallonsPerStroke);

                    currPump.bblStk = Number(currPumpbblStk.toFixed(4));
                    currPump.bblMin = Number(currPumpbblMin.toFixed(4));
                    currPump.galMin = Math.round(currPumpgalMin);
                }
            });
        }

        const drillStringSections = this.calcs.convertDrillStringAssembly(test.drillStringAssembly, test.depthBit);
        const intervalSections = this.calcs.convertIntervals(test.intervals, test.bitData.estHoleSize);
        const pipeSections = this.calcs.calcPipeSections(drillStringSections, intervalSections);

        let totalFlowGallonPM = 0;
        let totalFlowBarrelPM = 0;
        let strokesPM = 0;
        if ( test.pumpData.pumps.length > 0) {
            for (const pump of test.pumpData.pumps) {
                if (pump.onJob) {
                    totalFlowGallonPM += Number(pump.galMin);
                    totalFlowBarrelPM += Number(pump.bblMin);
                    strokesPM += Number(pump.stkMin);
                }
            }
        }

        const surfaceToBitBarrels = this.calcs.surfaceToBitVolumeBarrels({pipeSections});

        const bottomUpBarrels = this.calcs.bottomUpVolumeBarrels({pipeSections});

        const surfaceToSurfaceBarrels = this.calcs.surfaceToSurfaceVolumeBarrels({
            bottomUpVolume: bottomUpBarrels,
            surfaceToBitVolume: surfaceToBitBarrels,
        });

        const activeVolumeBarrels = this.calcs.activeVolumeBarrels({
            pitVolume: Number(test.pumpData.pitVolume),
            surfaceToSurfaceVolume: surfaceToSurfaceBarrels,
        });

        const volumeBelowBitBarrels = this.calcs.belowBitVolumeBarrels({pipeSections});
        const holeCapacity = this.calcs.holeCapacityBarrels({
            belowBitVolume: volumeBelowBitBarrels,
            surfaceToSurfaceVolume: surfaceToSurfaceBarrels,
        });
        const highLowAreaSections = this.calcs.getHighestLowestAreaPipeSections(pipeSections);
        const annularVelocityPump = this.calcs.calculateAnnularVelocities(highLowAreaSections, totalFlowGallonPM);

        const criticalVelocityPump = highLowAreaSections ? this.calcs.criticalVelocity({
            holeDiameter: highLowAreaSections.lowestFlowSection.holeDiameter,
            kValue,
            mudWeight: Number(test.mudProperties.mudWeight),
            nValue,
            pipeDiameter: highLowAreaSections.lowestFlowSection.outerPipeDiameter,
        }) : NaN;

        const effectiveViscosity = this.calcs.calculateEffectiveViscosity({
            annularVelocity: annularVelocityPump ? annularVelocityPump.lowestFlowAnnularVelocity : NaN,
            holeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.holeDiameter : NaN,
            kValue,
            nValue,
            pipeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.outerPipeDiameter : NaN,
        });

        const reynoldsNumber = this.calcs.calculateReynoldsNumber({
            annularVelocity: annularVelocityPump ? annularVelocityPump.lowestFlowAnnularVelocity : NaN,
            effectiveViscosity,
            holeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.holeDiameter : NaN,
            mudWeight: Number(test.mudProperties.mudWeight),
            nValue,
            pipeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.outerPipeDiameter : NaN,
        });

        const surfaceToBitMinutes = surfaceToBitBarrels / totalFlowBarrelPM;
        const bottomsUpMinutes = bottomUpBarrels / totalFlowBarrelPM;
        const surfaceToSurfaceMinutes = surfaceToSurfaceBarrels / totalFlowBarrelPM;
        const activeVolumeMinutes = activeVolumeBarrels / totalFlowBarrelPM;

        const surfaceToBitStrokes = surfaceToBitMinutes * strokesPM;
        const bottomsUpStrokes = bottomsUpMinutes * strokesPM;
        const surfaceToSurfaceStrokes = surfaceToSurfaceMinutes * strokesPM;
        const activeVolumeStrokes = activeVolumeMinutes * strokesPM;
        test.pumpData.activeVolumeBarrels = Math.round(isNaN(activeVolumeBarrels) ? 0 : activeVolumeBarrels);  
        test.pumpData.activeVolumeMinutes = Math.round(isNaN(activeVolumeMinutes) ? 0 : activeVolumeMinutes);
        test.pumpData.activeVolumeStks = Math.round(isNaN(activeVolumeStrokes) ? 0 : activeVolumeStrokes);
        test.pumpData.avdp = Math.round(isNaN(annularVelocityPump ? annularVelocityPump.lowestFlowAnnularVelocity : NaN)
        ? 0 : annularVelocityPump ? annularVelocityPump.lowestFlowAnnularVelocity : 0);
        test.pumpData.avdc = Math.round(isNaN(annularVelocityPump ? annularVelocityPump.highestFlowAnnularVelocity : NaN)
        ? 0 : annularVelocityPump ? annularVelocityPump.highestFlowAnnularVelocity : 0);
        test.pumpData.bottomsUpBarrels = Math.round(isNaN(bottomUpBarrels) ? 0 : bottomUpBarrels);
        test.pumpData.bottomsUpStks = Math.round(isNaN(bottomsUpStrokes) ? 0 : bottomsUpStrokes);
        test.pumpData.bottomsUpTime = Math.round(isNaN(bottomsUpMinutes) ? 0 : bottomsUpMinutes);
        test.pumpData.criticalVelocity = Math.round(isNaN(criticalVelocityPump) ? 0 : criticalVelocityPump);
        test.pumpData.holeCapacity = Math.round(isNaN(holeCapacity) ? 0 : holeCapacity);
        test.pumpData.surfaceToBitBarrels = Math.round(isNaN(surfaceToBitBarrels) ? 0 : surfaceToBitBarrels);
        test.pumpData.surfaceToBitMinutes = Math.round(isNaN(surfaceToBitMinutes) ? 0 : surfaceToBitMinutes);
        test.pumpData.surfaceToBitStks = Math.round(isNaN(surfaceToBitStrokes) ? 0 : surfaceToBitStrokes);
        test.pumpData.surfaceToSurfaceBarrels = Math.round(isNaN(surfaceToSurfaceBarrels) ? 0 : surfaceToSurfaceBarrels);
        test.pumpData.surfaceToSurfaceMinutes = Math.round(isNaN(surfaceToSurfaceMinutes) ? 0 : surfaceToSurfaceMinutes);
        test.pumpData.surfaceToSurfaceStks = Math.round(isNaN(surfaceToSurfaceStrokes) ? 0 : surfaceToSurfaceStrokes);
        test.pumpData.totalBblMin = Math.round(isNaN(totalFlowBarrelPM) ? 0 : totalFlowBarrelPM);
        test.pumpData.totalGalMin = Math.round(isNaN(totalFlowGallonPM) ? 0 : totalFlowGallonPM);
        test.pumpData.totalStksMin = Math.round(isNaN(strokesPM) ? 0 : strokesPM);
        test.pumpData.volumeBelowBit = Math.round(isNaN(volumeBelowBitBarrels) ? 0 : volumeBelowBitBarrels);
        test.pumpData.flowType = this.getFlowType(reynoldsNumber);

    }

    public calculateFluidProperties(test: ITest) {
        const chlorideCM3 = Number(test.mudProperties.chlorideClsn) / 10000;
        const nValue = this.calcs.nValue(
            {
                theta3: Number(test.mudProperties.rpmDegF3),
                theta300: Number(test.mudProperties.rpmDegF300),
            });

        const kValue = this.calcs.kValue(
            {
                nValue,
                theta300: Number(test.mudProperties.rpmDegF300)
            });

        const plasticViscosity = this.calcs.calcPlasticViscosity({
            theta300: Number(test.mudProperties.rpmDegF300),
            theta600: Number(test.mudProperties.rpmDegF600),
        });

        const hydrostaticPressure = this.calcs.hydrostaticPressure({
            depthTVD: Number(test.depthTVD),
            mudWeight: Number(test.mudProperties.mudWeight),
        });

        const yieldPoint = this.calcs.calcYieldPoint({
            plasticViscosity,
            theta300: Number(test.mudProperties.rpmDegF300),
        });

        const mudGradient = this.calcs.mudGradient({
            mudWeight: Number(test.mudProperties.mudWeight)
        });

        const drillStringSections = this.calcs.convertDrillStringAssembly(test.drillStringAssembly, test.depthBit);
        const intervalSections = this.calcs.convertIntervals(test.intervals, test.bitData.estHoleSize);
        const pipeSections = this.calcs.calcPipeSections(drillStringSections, intervalSections);
        
        const surgeSwabPressureDrop = this.calcs.surgeSwabPressureLossMethod1({
            isPipeOpen: !test.bitData.isPipeClosed,
            pipeSections,
            theta300: Number(test.mudProperties.rpmDegF300),
            theta600: Number(test.mudProperties.rpmDegF600),
            tripSpeed: Number(test.bitData.tripSpeed),
        });
        const surgePressure = this.calcs.surgePressure({
            depthTVD: Number(test.depthTVD),
            mudWeight: Number(test.mudProperties.mudWeight),
            pressureLoss: surgeSwabPressureDrop,
        });

        const swabPressure = this.calcs.swabPressure({
            depthTVD: Number(test.depthTVD),
            mudWeight: Number(test.mudProperties.mudWeight),
            pressureLoss: surgeSwabPressureDrop,
        });

        let totalFlowGallonPM = 0;
        if ( test.pumpData.pumps.length > 0) {
            for (const pump of test.pumpData.pumps) {
                if (pump.onJob) {
                    totalFlowGallonPM += Number(pump.galMin);
                }
            }
        }

        const annularPressureLoss = this.calcs.calculateAnnularPressureLoss({
            circRate: test.pumpData.totalGalMin,
            mudWeight: test.mudProperties.mudWeight,
            pipeSections
        });

        const ecd = this.calcs.ECD({
            annularPressureLoss: Number(annularPressureLoss),
            depthTVD: Number(test.depthTVD),
            mudWeight: Number(test.mudProperties.mudWeight),
        });

        const highLowAreaSections = this.calcs.getHighestLowestAreaPipeSections(pipeSections);
        const annularVelocityPump = this.calcs.calculateAnnularVelocities(highLowAreaSections, totalFlowGallonPM);

        const effectiveViscosity = this.calcs.calculateEffectiveViscosity({
            annularVelocity: annularVelocityPump ? annularVelocityPump.lowestFlowAnnularVelocity : NaN,
            holeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.holeDiameter : NaN,
            kValue,
            nValue,
            pipeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.outerPipeDiameter : NaN,
        });

        const reynoldsNumber = this.calcs.calculateReynoldsNumber({
            annularVelocity: annularVelocityPump ? annularVelocityPump.lowestFlowAnnularVelocity : NaN,
            effectiveViscosity,
            holeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.holeDiameter : NaN,
            mudWeight: Number(test.mudProperties.mudWeight),
            nValue,
            pipeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.outerPipeDiameter : NaN,
        });

        const retortContentSolidsPercent = this.calcs.calculateRetortContentSolidsPercent({
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const oilRatio = this.calcs.calculateOilRatio({
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const waterRatio = this.calcs.calculateWaterRatio({
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const adjustedSolidsPercentOil = this.calcs.calculateAdjustedSolidsPercentOil({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            mudWeight: Number(test.mudProperties.mudWeight),
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const saltWaterPhase = this.calcs.calculateSaltWaterPhase({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const chlorideMilsPerLiter = this.calcs.calculateChlorideMilsPerLiter({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const cacl2wps = this.calcs.calculateCaCL2mgLWPS({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const averageSpecificGravityOil = this.calcs.calculateAverageSpecificGravityOil({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            drillSolidsSpecificGravity: Number(test.mudProperties.drillSolidsSg),
            mudWeight: Number(test.mudProperties.mudWeight),
            oilSpecificGravity: Number(test.mudProperties.oilSg),
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
            wtMaterialSpecificGravity: Number(test.mudProperties.wghtMatlSg),
        });

        const lowGravityPercent = this.calcs.calculateLowGravityPercent({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            drillSolidsSpecificGravity: Number(test.mudProperties.drillSolidsSg),
            mudWeight: Number(test.mudProperties.mudWeight),
            oilSpecificGravity: Number(test.mudProperties.oilSg),
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
            wtMaterialSpecificGravity: Number(test.mudProperties.wghtMatlSg),
        });

        const lowGravityPPB = this.calcs.calculateLowGravityPPB({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            drillSolidsSpecificGravity: Number(test.mudProperties.drillSolidsSg),
            mudWeight: Number(test.mudProperties.mudWeight),
            oilSpecificGravity: Number(test.mudProperties.oilSg),
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
            wtMaterialSpecificGravity: Number(test.mudProperties.wghtMatlSg),
        });

        const highGravityPercent = this.calcs.calculateHighGravityPercent({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            drillSolidsSpecificGravity: Number(test.mudProperties.drillSolidsSg),
            mudWeight: Number(test.mudProperties.mudWeight),
            oilSpecificGravity: Number(test.mudProperties.oilSg),
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
            wtMaterialSpecificGravity: Number(test.mudProperties.wghtMatlSg),
        });

        const highGravityPPG = this.calcs.calculateHighGravityPPG({
            alkalinity: Number(test.mudProperties.mudAlkalinityPom),
            chlorideCM3,
            drillSolidsSpecificGravity: Number(test.mudProperties.drillSolidsSg),
            mudWeight: Number(test.mudProperties.mudWeight),
            oilSpecificGravity: Number(test.mudProperties.oilSg),
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            versenateCM3: Number(test.mudProperties.versenate),
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
            wtMaterialSpecificGravity: Number(test.mudProperties.wghtMatlSg),
        });


        const calcMagnesium = this.calcs.calcMagnesium({
            calcium: Number(test.mudProperties.calcium),
            totalHardness: Number(test.mudProperties.totalHardness)
        });

        const excessLimeWater = this.calcs.excessLimeWater({
            filtrateAlkalinity: Number(test.mudProperties.filtrateAlkalinityPf),
            fractionWater: Number(test.mudProperties.retortContentWater) / 100,
            mudAlkalinity: Number(test.mudProperties.mudAlkalinityPm)
        });

        const excessLimeOil = this.calcs.excessLimeOil({
            alkalinity:Number(test.mudProperties.mudAlkalinityPom)
        });

        const saltPercentByVolume = this.calcs.calculateSaltPercentByVolume({
            chlorideMilsPerLiter: Number(test.mudProperties.chlorideMgL),
            saltType: test.propertyType,
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const adjSolidsPercent = this.calcs.calculateAdjustedSolidsPercentWater({
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            saltPercentByVolume,
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const adjustedChloride = this.calcs.calculateAdjustedChloride({
            chlorideMilsPerLiter: Number(test.mudProperties.chlorideMgL),
            saltPercentByVolume,
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const saltPercentWW = this.calcs.calculateSaltPercentWW({
            adjustedChloride,
            saltType: test.propertyType,
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        const brineSpecificGravity = this.calcs.calculateBrineSpecificGravity({
            saltPercentWW,
            saltType: test.propertyType,
        });

        const averageSpecificGravityWater = this.calcs.calculateAverageSpecificGravityWater({
            adjSolidsPercent,
            adjustedChloride,
            brineSpecificGravity,
            chlorideMilsPerLiter: Number(test.mudProperties.chlorideMgL),
            mudWeight: Number(test.mudProperties.mudWeight),
            oilSpecificGravity: Number(test.mudProperties.oilSg),
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            saltPercentByVolume,
            saltPercentWW,
            saltType: test.propertyType,
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
        });

        if (test.weighted === false && !test.mudProperties.drillSolidsSgOverride)
        {
            test.mudProperties.drillSolidsSg = Number.isFinite(averageSpecificGravityWater) ? Number(averageSpecificGravityWater.toFixed(4)) : 0;
        }
        const lowGravityPercentWater = this.calcs.calculateLowGravityPercentWater({
            adjSolidsPercent,
            adjustedChloride,
            brineSpecificGravity,
            chlorideMilsPerLiter: Number(test.mudProperties.chlorideMgL),
            lowGravitySolidSpecificGravity: Number(test.mudProperties.drillSolidsSg),
            mudWeight: Number(test.mudProperties.mudWeight),
            oilSpecificGravity: Number(test.mudProperties.oilSg),
            oilVolumePercent: Number(test.mudProperties.retortContentOil),
            saltPercentByVolume,
            saltPercentWW,
            saltType: test.propertyType,
            waterVolumePercent: Number(test.mudProperties.retortContentWater),
            wtMaterialSpecificGravity: Number(test.mudProperties.wghtMatlSg),
        });

        const highGravityPercentWater = this.calcs.calculateHighGravityPercentWater({
            adjSolidsPercent,
            lowGravityPercent: lowGravityPercentWater
        });

        const lowGravityPPBWater = this.calcs.calculateLowGravityPPBWater({
            gravityPercent: lowGravityPercentWater,
            gravitySolidSpecificGravity: Number(test.mudProperties.drillSolidsSg),
        });

        const highGravityPPBWater = this.calcs.calculateHighGravityPPBWater({
            gravityPercent: highGravityPercentWater,
            gravitySolidSpecificGravity: Number(test.mudProperties.wghtMatlSg),
        });

        const bentonitePPB = this.calcs.calculateBentonitePPB({
            cationExchangeCapacity: Number(test.mudProperties.cationExchangeCapacity),
            lowGravityPPB: lowGravityPPBWater,
            methyleneBlueTest: Number(test.mudProperties.methyleneBlueTest),
        });

        const bentonitePercent = this.calcs.calculateBentonitePercent({
            bentonitePPB,
            lowGravitySolidSpecificGravity: Number(test.mudProperties.drillSolidsSg),
        });

        const drillSolidsPercent = this.calcs.calculateDrillSolidsPercent({
            bentonitePercent,
            lowGravityPercent: lowGravityPercentWater,
        });

        const drillSolidsPPB = this.calcs.calculateDrillSolidsPPB({
            bentonitePPB,
            lowGravityPPB: lowGravityPPBWater,
        });


        test.mudProperties.plasticViscosity = isNaN(plasticViscosity) ? 0 : Number(plasticViscosity.toFixed(2));
        test.mudProperties.yeildPoint = isNaN(yieldPoint) ? 0 : Number(yieldPoint.toFixed(2));
        test.mudProperties.mudGradient = isNaN(mudGradient) ? 0 : Number(mudGradient.toFixed(2));
        test.mudProperties.hydrostaticPsi = isNaN(hydrostaticPressure) ? 0 : Number(hydrostaticPressure.toFixed(2));
        test.mudProperties.ecd = isNaN(ecd) ? 0 : Number(ecd.toFixed(3));
        test.mudProperties.surgePressure = isNaN(surgePressure) ? 0 : Number(surgePressure.toFixed(2));
        test.mudProperties.swabPressure = isNaN(swabPressure) ? 0 : Number(swabPressure.toFixed(2));
        test.mudProperties.nValue = isNaN(nValue) ? 0 : Number(nValue.toFixed(2));
        test.mudProperties.kValue = isNaN(kValue) ? 0 : Number(kValue.toFixed(2));
        test.mudProperties.reynoldsNbr = isNaN(reynoldsNumber) ? 0 : Number(reynoldsNumber.toFixed(2));
        test.mudProperties.retortContentSolids = isNaN(retortContentSolidsPercent) ? 0 : Number(retortContentSolidsPercent.toFixed(4));
        test.mudProperties.oilWaterRatioFrom = isNaN(oilRatio ) ? 0 : Number(oilRatio.toFixed(4));
        test.mudProperties.oilWaterRatioTo = isNaN(waterRatio ) ? 0 : Number(waterRatio.toFixed(4));
        test.mudProperties.saltVolumePcnt = isNaN(adjustedSolidsPercentOil) ? 0 : Number(adjustedSolidsPercentOil.toFixed(4));
        test.mudProperties.limeContent = isNaN(excessLimeOil) ? 0 : Number(excessLimeOil.toFixed(2));
        test.mudProperties.excessLime = isNaN(excessLimeWater) ? 0 : Number(excessLimeWater.toFixed(2));
        test.mudProperties.effectiveViscosity = isNaN(effectiveViscosity) ? 0 : Number(effectiveViscosity.toFixed(2));
        if ( test.mudProperty === 'Oil Based Mud') {
            test.mudProperties.asg = isNaN(averageSpecificGravityOil) ? 0 : Number(averageSpecificGravityOil.toFixed(4));
            test.mudProperties.saltContentWaterPhase = isNaN(saltWaterPhase) ? 0 : Number(saltWaterPhase.toFixed(4));
            test.mudProperties.chlorideMgL = isNaN(chlorideMilsPerLiter) ? 0 : Number(chlorideMilsPerLiter.toFixed(0));
            test.mudProperties.lowGravityPcnt = isNaN(lowGravityPercent) ? 0 : Number(lowGravityPercent.toFixed(4));
            test.mudProperties.lowGravityLbBbl = isNaN(lowGravityPPB) ? 0 : Number(lowGravityPPB.toFixed(4));
            test.mudProperties.highGravityPcnt = isNaN(highGravityPercent) ? 0 : Number(highGravityPercent.toFixed(4));
            test.mudProperties.highGravityLbBbl = isNaN(highGravityPPG) ? 0 : Number(highGravityPPG.toFixed(4));
            test.mudProperties.cacl2wps = isNaN(cacl2wps) ? 0 : Number(cacl2wps.toFixed(0));
        }
        else {
            test.mudProperties.asg = isNaN(averageSpecificGravityWater) ? 0 : Number(averageSpecificGravityWater.toFixed(4));
            test.mudProperties.lowGravityPcnt = isNaN(lowGravityPercentWater) ? 0 : Number(lowGravityPercentWater.toFixed(4));
            test.mudProperties.lowGravityLbBbl = isNaN(lowGravityPPBWater) ? 0 : Number(lowGravityPPBWater.toFixed(4));
            test.mudProperties.highGravityPcnt = isNaN(highGravityPercentWater) ? 0 : Number(highGravityPercentWater.toFixed(4));
            test.mudProperties.highGravityLbBbl = isNaN(highGravityPPBWater) ? 0 : Number(highGravityPPBWater.toFixed(4));
            
            if(test.mudProperties.calcium === '' && test.mudProperties.totalHardness === '') {
                test.mudProperties.magnesium = '';
            } else {
                test.mudProperties.magnesium = isNaN(calcMagnesium) ? '' : Number(calcMagnesium.toFixed(4));
            }
            
            test.mudProperties.excessLime = isNaN(excessLimeWater) ? 0 : Number(excessLimeWater.toFixed(4));
            test.mudProperties.saltVolumePcnt = isNaN(saltPercentByVolume) ? 0 : Number(saltPercentByVolume.toFixed(4));
            test.mudProperties.bentonitePcnt = isNaN(bentonitePercent) ? 0 : Number(bentonitePercent.toFixed(4));
            test.mudProperties.bentoniteLbBbl = isNaN(bentonitePPB) ? 0 : Number(bentonitePPB.toFixed(4));
            test.mudProperties.drillSolidsPcnt = isNaN(drillSolidsPercent) ? 0 : Number(drillSolidsPercent.toFixed(4));
            test.mudProperties.drillSolidsLbBbl = isNaN(drillSolidsPPB) ? 0 : Number(drillSolidsPPB.toFixed(4));
            if (test.propertyType === 'Brine Emulsion') {

                const percentSaltBrine = this.calcs.calculateSaltPercentBrine({
                    chlorideMilsPerLiter: Number(test.mudProperties.chlorideMgL),
                });

                const percentSaltAdjBrine = this.calcs.calculateSaltPercentAdjBrine({
                    chlorideMilsPerLiter: Number(test.mudProperties.chlorideMgL),
                    waterVolumePercent: Number(test.mudProperties.retortContentWater),
                });

                const drillSolidsPercentBrine = this.calcs.calculateDrillSolidsBrine({
                    saltPercentAdjBrineEmulsion: percentSaltAdjBrine,
                    solidVolumePercent: Number(test.mudProperties.retortContentSolids),
                });
                test.mudProperties.saltVolumePcnt = isNaN(percentSaltBrine) ? 0 : Number(percentSaltBrine.toFixed(2));
                test.mudProperties.saltVolumeAdjPcnt = isNaN(percentSaltAdjBrine) ? 0 : Number(percentSaltAdjBrine.toFixed(2));
                test.mudProperties.drillSolidsPcnt = isNaN(drillSolidsPercentBrine) ? 0 : Number(drillSolidsPercentBrine.toFixed(2));
            }


        }
        if (test.mudProperties.highGravityPcnt < 0.1 && test.mudProperties.highGravityPcnt > -0.1) {
            test.mudProperties.highGravityPcnt = 0;
            test.mudProperties.highGravityLbBbl = 0;
        }
    }

    public calculateBitData(test: ITest) {
        const nValue = this.calcs.nValue(
            {
                theta3: Number(test.mudProperties.rpmDegF3),
                theta300: Number(test.mudProperties.rpmDegF300),
            });

        const kValue = this.calcs.kValue(
            {
                nValue,
                theta300: Number(test.mudProperties.rpmDegF300)
            });

        let nozzleVelocity = 0;
        let jetImpactForce = 0;
        let bitPressureDrop = 0;
        let bitHHP = 0;
        let bitHHPpSI = 0;
        if (Array.isArray(test.bitData.bits) && test.bitData.bits.length > 0) {

            nozzleVelocity = this.calcs.nozzleVelocity({
                circRate: Number(test.pumpData.totalGalMin),
                jetSizes: test.bitData.bits[0].jetSizes,
            });

            jetImpactForce = this.calcs.jetImpactForce({
                circRate: Number(test.pumpData.totalGalMin),
                mudWeight: Number(test.mudProperties.mudWeight),
                nozzleVelocity,
            });

            bitPressureDrop = this.calcs.jetNozzlePressureDrop({
                circRate: Number(test.pumpData.totalGalMin),
                jetSizes: test.bitData.bits[0].jetSizes,
                mudWeight:Number(test.mudProperties.mudWeight),
            });
            bitHHP = this.calcs.bitHydraulicHp({
                circRate: Number(test.pumpData.totalGalMin),
                jetNozzlePressureDrop: bitPressureDrop,

            });

            bitHHPpSI = this.calcs.bitHydraulicHpPerSquareInch({
                bitHydraulicHp: Number(bitHHP),
                bitSize: Number(test.bitData.bits[0].bitSize),
            });
            test.bitData.estHoleSize = Number((Number(test.bitData.bits[0].bitSize) + Number(test.bitData.washout)).toFixed(3));
            if (Array.isArray(test.intervals) && test.intervals[0] && !test.isNewIntervalReport) {
                if (test.intervals[0].estHoleSize) {
                    test.intervals[0].estHoleSize = test.bitData.estHoleSize;
                }
            }
        }


        const percentPressureDrop = this.calcs.percentPressureDrop({
            jetNozzlePressureDrop: bitPressureDrop,
            surfacePressure: Number(test.pumpData.circPressure),
        });
        this.calcs.adjustDrillStringAssembly(test.drillStringAssembly, test.depthBit);
        const drillStringSections = this.calcs.convertDrillStringAssembly(test.drillStringAssembly, test.depthBit);
        const intervalSections = this.calcs.convertIntervals(test.intervals, test.bitData.estHoleSize);
        const pipeSections = this.calcs.calcPipeSections(drillStringSections, intervalSections);
        const surgeSwabPressureDrop = this.calcs.surgeSwabPressureLossMethod1({
            isPipeOpen: !test.bitData.isPipeClosed,
            pipeSections,
            theta300: Number(test.mudProperties.rpmDegF300),
            theta600: Number(test.mudProperties.rpmDegF600),
            tripSpeed: Number(test.bitData.tripSpeed),
        });
        const surgePressure = this.calcs.surgePressure({
            depthTVD: Number(test.depthTVD),
            mudWeight: Number(test.mudProperties.mudWeight),
            pressureLoss: surgeSwabPressureDrop,
        });

        const swabPressure = this.calcs.swabPressure({
            depthTVD: Number(test.depthTVD),
            mudWeight: Number(test.mudProperties.mudWeight),
            pressureLoss: surgeSwabPressureDrop,
        });


        let totalFlowGallonPM = 0;
        if ( test.pumpData.pumps.length > 0) {
            for (const pump of test.pumpData.pumps) {
                if (pump.onJob) {
                    totalFlowGallonPM += Number(pump.galMin);
                }
            }
        }

        const annularPressureLoss = this.calcs.calculateAnnularPressureLoss({
            circRate: test.pumpData.totalGalMin,
            mudWeight: test.mudProperties.mudWeight,
            pipeSections
        });

        const ecd = this.calcs.ECD({
            annularPressureLoss: Number(annularPressureLoss),
            depthTVD: Number(test.depthTVD),
            mudWeight: Number(test.mudProperties.mudWeight),
        });

        const highLowAreaSections = this.calcs.getHighestLowestAreaPipeSections(pipeSections);
        const annularVelocityPump = this.calcs.calculateAnnularVelocities(highLowAreaSections, totalFlowGallonPM);

        const effectiveViscosity = this.calcs.calculateEffectiveViscosity({
            annularVelocity: annularVelocityPump ? annularVelocityPump.lowestFlowAnnularVelocity : NaN,
            holeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.holeDiameter : NaN,
            kValue,
            nValue,
            pipeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.outerPipeDiameter : NaN,
        });

        const reynoldsNumber = this.calcs.calculateReynoldsNumber({
            annularVelocity: annularVelocityPump ? annularVelocityPump.lowestFlowAnnularVelocity : NaN,
            effectiveViscosity,
            holeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.holeDiameter : NaN,
            mudWeight: Number(test.mudProperties.mudWeight),
            nValue,
            pipeDiameter: highLowAreaSections ? highLowAreaSections.lowestFlowSection.outerPipeDiameter : NaN,
        });

        test.bitData.ecd = isNaN(ecd) ? 0 : Number(ecd.toFixed(3));
        test.bitData.nozzleVelocity = isNaN(nozzleVelocity) ? 0 : Number(nozzleVelocity.toFixed(2));
        test.bitData.jetImpactForce = isNaN(jetImpactForce) ? 0 : Number(jetImpactForce.toFixed(2));
        test.bitData.hydraulicHorsepower = isNaN(bitHHPpSI) ? 0 : Number(bitHHPpSI.toFixed(2));
        test.bitData.pressureDrop = isNaN(bitPressureDrop) ? 0 : Number(bitPressureDrop.toFixed(2));
        test.bitData.percentPressureDrop = isNaN(percentPressureDrop) ? 0 : Number(percentPressureDrop.toFixed(2));
        test.bitData.surgePressure = isNaN(surgePressure) ? 0 : Number(surgePressure.toFixed(2));
        test.bitData.swabPressure = isNaN(swabPressure) ? 0 : Number(swabPressure.toFixed(2));
        test.bitData.nValue = isNaN(nValue) ? 0 : Number(nValue.toFixed(2));
        test.bitData.kValue = isNaN(kValue) ? 0 : Number(kValue.toFixed(2));
        test.bitData.reynoldsNumber = isNaN(reynoldsNumber) ? 0 : Number(reynoldsNumber.toFixed(2));
    }

    public updateDrillStringAndInterval = (test: ITest) => {
        if (test.intervals) {
            test.intervals.forEach((value) => {
                const innerDiameter = value.casingID ? Number(value.casingID) : Number(test.bitData.estHoleSize);
                const calcCap = this.calcs.capacityInBarrelsPerFoot({
                    innerDiameter,
                    length: 1,
                });
                value.capBblLinFt = isNaN(calcCap) ? 0 : Number(calcCap.toFixed(4));
            });
        }

        if (test.drillStringAssembly) {
            test.drillStringAssembly.forEach((value) => {
                const calcCap = this.calcs.capacityInBarrelsPerFoot({
                    innerDiameter: Number(value.innerDiameter),
                    length: 1,
                });

                const calcDisp = this.calcs.displacementInBarrelsPerFoot({
                    innerDiameter: Number(value.innerDiameter),
                    length: 1,
                    outerDiameter: Number(value.outerDiameter),
                });
                value.cap = isNaN(calcCap) ? 0 : Number(calcCap.toFixed(4));
                value.disp = isNaN(calcDisp) ? 0 : Number(calcDisp.toFixed(4));
            });
        }
    }

    public testDrillStringAndInterval = (test: ITest) => {
        if (test.intervals && test.drillStringAssembly) {
            const intervalSections = this.calcs.convertIntervals(test.intervals, test.bitData.estHoleSize);
            const drillStringSections = this.calcs.convertDrillStringAssembly(test.drillStringAssembly, test.depthBit);
            const pipeSections = this.calcs.calcPipeSections(drillStringSections, intervalSections);
            return pipeSections !== undefined;
        }
        return false;
    }

    public isDisplacementDisabled = (test: ITest) => {
        if (!this.tests) {
            return true;
        }
        
        if (!test.isVolumeAccountingReport) {
            return true;
        }
        
        if (test.volumeAccounting.fluidChanged) {
            return false;
        }
        
        if (test.mudProperty === 'Water Based Mud' && !test.volumeAccounting.trackWaterBasedVolume) {
            return true;
        }
        const lastTestIndex = this.tests.length - 1;
        const testIndex = this.tests.findIndex(t => t.id === test.id);
        
        let isDisplacementReport = true;
        let fluidChange = true;
        let prevFluidType = "";
        let prevMudProperty = "";
        for (let i = lastTestIndex; i > testIndex; i--)
        {

            if (prevFluidType !== this.tests[i].fluidType 
                || prevMudProperty !== this.tests[i].mudProperty) {
                fluidChange = true;
                isDisplacementReport = true;
            }

            const trackWaterVol = i === testIndex ? test.volumeAccounting.trackWaterBasedVolume : this.tests[i].trackWaterBasedVolume;
            if (
                this.tests[i].isVolumeAccountingReport
                && ( 
                    this.tests[i].mudProperty === 'Oil Based Mud'
                    || (
                        this.tests[i].mudProperty === 'Water Based Mud' && trackWaterVol
                    )
                )
                && fluidChange
            )
            {
                isDisplacementReport = false;
                fluidChange = false;
            }
            
            

            prevFluidType = this.tests[i].fluidType;
            prevMudProperty = this.tests[i].mudProperty;
        }

        if (prevFluidType !== this.tests[testIndex].fluidType 
            || prevMudProperty !== this.tests[testIndex].mudProperty) {
            fluidChange = true;
            isDisplacementReport = true;
        }

        return !isDisplacementReport;
    }

    public updateVolumeCalculations = (test: ITest) => {
        const va = test.volumeAccounting;
        if (!va) { return; }

        if (!va.externalStorageUserOverride) {
            let tempExternalstorage = 0;
            if (test.externalStorage) {
                test.externalStorage.forEach((value) => {
                    const testFluidType = test.mudProperty === 'Oil Based Mud' ? "OBM" : "WBM";
                    const trackingVolume = ( test.mudProperty === 'Water Based Mud' && va.trackWaterBasedVolume ) || test.mudProperty === 'Oil Based Mud';
                    if( value.inUse && value.fluidType === testFluidType && trackingVolume) {
                        tempExternalstorage += Number(value.todayFluidAmount);
                    }
                });
                va.externalStorage = tempExternalstorage;
            }
        }

        if (!va.totalHoleVolumeUserOverride) {
            va.totalHoleVolume = test.pumpData.holeCapacity;
        }

        if (!va.chemicalAdditionsUserOverride) {
            // Do the chemical additions calc here
                const remainingChemicalAdditions = Number.isFinite(Number(va.remainingChemicalAdditions)) ? Number(va.remainingChemicalAdditions) : 0;
                va.chemicalAdditions = Math.round(this.calcs.calculateChemicalAddition(test.productInventorys) + remainingChemicalAdditions);
        } 
        
        if (test.isVolumeAccountingReport && (va.trackWaterBasedVolume || test.mudProperty === 'Oil Based Mud')) {

            let previousDepth = Number(va.previousDepthMD);
            if (test.isNewIntervalReport && test.intervals[1] && Number(test.intervals[1].plugBackDepth) > 0) {
                previousDepth = Number(test.intervals[1].plugBackDepth);
            }
            if (this.isDisplacementDisabled(test)) {
                va.newHoleVolume = Math.round(this.calcs.calculateNewHoleVolume({
                    currentDepthMD: Number(test.depthMD),
                    estimatedHoleSize: Number(test.bitData.estHoleSize),
                    previousDepthMD: Number(previousDepth)
                }));
                va.footageDrilled = Math.round(this.calcs.calculateFootageDrilled({
                    currentDepthMD: Number(test.depthMD),
                    previousDepthMD: Number(previousDepth),
                }));
            } else if (Number(va.displacement) !== 0) {
                va.newHoleVolume = Math.round(this.calcs.calculateNewHoleVolume({
                    currentDepthMD: Number(test.depthMD),
                    estimatedHoleSize: Number(test.bitData.estHoleSize),
                    previousDepthMD: Number(va.displacement)
                }));
                va.footageDrilled = Number(test.depthMD - va.displacement);
            } else {
                va.newHoleVolume = 0;
            }
            


            if (va.newHoleVolume < 0) {
                va.newHoleVolume = 0;
            }
    
            if (va.footageDrilled < 0) {
                va.footageDrilled = 0;
            }
            if (!va.volOwedToPlantUserOverride) {
                va.volOwedToPlant = Math.round(this.calcs.calculateVolumeOwedToPlant({
                    previousVolumeOwed: Number(va.previousVolOwedToPlant),
                    volumeReceived: Number(va.plantVolRecvd),
                    volumeReturned: Number(va.plantVolReturned),
                }));
            }

            va.lossToCuttings = Math.round(this.calcs.calculateLossesToCuttings({
                lossCuttingRatio: Number(va.lostToCuttingsRatio),
                newHoleVolume: Number(va.newHoleVolume),
            }));
        } else {
            va.newHoleVolume = 0;
            va.footageDrilled = 0;
            va.volOwedToPlant = 0;
            va.lossToCuttings = 0;
        }

        



        va.baseOilUsed = Math.round(this.calcs.calculateBaseOilUsed({
            baseOilReceived: Number(va.baseOilReceived),
            endingBaseOil: Number(va.endingBaseOil),
            startingBaseOil: Number(va.startingBaseOil),
        }));
        va.cumBaseOilUsed = Math.round(this.calcs.calculateCumBaseOilUsed(Number(va.baseOilUsed), Number(va.prevCumBaseOilUsed)));
        va.baseOil = Math.round(this.calcs.calculateBaseOilUsedBarrels(Number(va.baseOilUsed)));
        va.totalAdditions = Math.round(this.calcs.calculateTotalAdditions({
            baseOilUsedBarrels: Number(va.baseOil),
            chemicalAdditions: test.isVolumeAccountingReport ? Number(va.chemicalAdditions) : 0,
            otherAdditions: Number(va.otherAdditions),
            otherReceived: Number(va.otherReceived),
            plantVolRecvd: va.fluidChanged ? 0 : Number(va.plantVolRecvd),  // mud-1576
            slurry: Number(va.slurry),
            washout: Number(va.washout),
            water: Number(va.water),
            xferFromStorage: Number(va.xferFromStorage)
        }));


        va.totalLosses = Math.round(this.calcs.calculateTotalLosses({
            lossCuttings: Number(va.lossToCuttings),
            lossFormation: Number(va.lossToFormation),
            lossSurface: Number(va.lossToSurface),
            mudInHole: Number(va.mudLeftInHole),
            otherLosses: Number(va.otherLoss),
            otherTransfered: Number(va.otherTransfer),
            plantVolReturned: va.fluidChanged ? 0 : Number(va.plantVolReturned),  // mud-1576
            solidsControl: Number(va.solidsControl),
        }));

        va.totalVolume = Math.round(this.calcs.calculateTotalVolume({
            externalStorage: Number(va.externalStorage),
            pvt: Number(test.pumpData.pitVolume),
            totalHoleVolume: Number(va.totalHoleVolume)
        }));

        if (!test.isVolumeAccountingReport) {
            va.totalVolume = va.previousTotalHoleVolume;
        }
        
        va.requiredReconciliation = Math.round(this.calcs.calculateRequiredReconciliation({
            previousTotalHoleVolume: Number(va.previousTotalHoleVolume),
            totalAdditions: Number(va.totalAdditions),
            totalLosses: Number(va.totalLosses),
            totalVolume: Number(va.totalVolume)
        }));

        for (const prop in va) {
            if( Number.isNaN(va[prop])) {
                va[prop] = 0;
            }
        }
    }

    public calculateEbpCosts = (test: ITest) => {
        if (test.ebpInventorys) {
            test.ebpInventorys.forEach(row => {
                
                const unitPrice: any = row.unitPrice.toString().replace(/[$,]/g, '');
                
                row.totalCost = row.quantity * unitPrice * Number(row.unit);
            });
        }
    }

    public calculateProductCosts = (test: ITest) => {
        if (test.productInventorys) {
            test.productInventorys.forEach(row => {
                const price = Number(row.priceUsed) || 0;
                const amountUsed = Number(row.amountUsed) || 0;
                row.totalCost = price * amountUsed;
            });
        }
    }

    public calculateTotalCosts = (test: ITest) => {
        test.inventoryTotal.productTotal = 0;
        test.inventoryTotal.productTax = 0;
        test.inventoryTotal.otherCosts = 0;
        test.inventoryTotal.otherCostTax = 0;
        test.inventoryTotal.cumulativeTotal = Number(test.inventoryTotal.prevCumulativeTotal);

        if ( test.productInventorys.length > 0 ) {
            test.productInventorys.forEach((product) => {
                test.inventoryTotal.productTotal += product.totalCost;
                const taxRate = test.inventoryTotal.productTaxRate / 100;
                if (product.isTaxable) {
                    test.inventoryTotal.productTax += product.totalCost * taxRate;
                }
            });
        }

        if ( test.ebpInventorys.length > 0 ) {
            test.ebpInventorys.forEach((expense) => {
                test.inventoryTotal.otherCosts += expense.totalCost;
                const serviceTaxRate = test.inventoryTotal.serviceTaxRate / 100;
                const rentalTaxRate = test.inventoryTotal.rentalTaxRate / 100;
                if (expense.isTaxable) {
                    if (expense.expenseType.isRental) {
                        test.inventoryTotal.otherCostTax += expense.totalCost * rentalTaxRate;
                    } else {
                        test.inventoryTotal.otherCostTax += expense.totalCost * serviceTaxRate;
                    }
                }
            });
        }
        
        test.inventoryTotal.dailyTotal = test.inventoryTotal.productTotal + test.inventoryTotal.productTax + test.inventoryTotal.otherCosts + test.inventoryTotal.otherCostTax;
        if (!test.isInventoryReport) {
            test.inventoryTotal.otherCosts = 0;
            test.inventoryTotal.otherCostTax = 0;
            test.inventoryTotal.dailyTotal = 0;
        }
        test.inventoryTotal.cumulativeTotal += Number(test.inventoryTotal.dailyTotal);
    }

    public redoCalculations() {
       
        runInAction(() => {
            this.calculateBitData(this.test);
            this.updateDrillStringAndInterval(this.test);
            this.calculateFluidProperties(this.test);
            this.calculatePumpData(this.test);
            this.updateVolumeCalculations(this.test);
            this.calculateEbpCosts(this.test);
            this.calculateProductCosts(this.test);
            this.calculateTotalCosts(this.test);
        });
    }

    private getFlowType(reynoldsNumber: number) {
        if (isNaN(reynoldsNumber)) {
            return 'Unknown';
        }
        let flowType = 'Laminar';
        if (reynoldsNumber > 2000) {
            if (reynoldsNumber > 4000) {
                flowType = 'Turbulent';
            } else {
                flowType = 'Transitional';
            }
        }
        return flowType;
    }
}



// WEBPACK FOOTER //
// ./src/Stores/Tests/TestsStore.tsx